import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Subject } from 'rxjs';
import { Dialog } from '@angular/cdk/dialog';
import { ErrorDialogComponent } from '@core/error-handling/components';
import { ApiErrorHandlerService } from '../api-error-handler/api-error-handler.service';
import { ErrorNotification } from '../api-error.model';
import { ErrorMetadata } from '../error-interceptor';

export interface ApiError {
  key: string;
  message: string;
  traceId?: string;
  additionalInformation?: string[];
}

@Injectable({ providedIn: 'root' })
export class ErrorManagerService {
  private errorSubject = new Subject<ErrorNotification>();
  public error$ = this.errorSubject.asObservable();

  private dialog = inject(Dialog);
  private apiErrorHandler = inject(ApiErrorHandlerService);

  handleError(error: unknown, metadata?: ErrorMetadata): void {
    console.error('Error:', error);

    if (error instanceof HttpErrorResponse && error.error instanceof Blob) {
      this.handleBlobHttpError(error, metadata);
    } else if (error instanceof HttpErrorResponse) {
      this.handleHttpError(error, metadata);
    } else {
      this.handleGenericError(error);
    }
  }

  private handleBlobHttpError(
    error: HttpErrorResponse,
    metadata?: ErrorMetadata
  ): void {
    const blob = error.error as Blob;
    blob.text().then((text) => {
      try {
        const errorResponse = JSON.parse(text) as {
          message?: string;
          key?: string;
        };
        error = new HttpErrorResponse({
          error: errorResponse,
          headers: error.headers,
          status: error.status,
          url: error.url ?? undefined,
        });

        this.handleHttpError(error, metadata);
      } catch {
        this.handleGenericError(error);
      }
    });
  }

  private handleHttpError(
    error: HttpErrorResponse,
    metadata?: ErrorMetadata
  ): void {
    const result = this.apiErrorHandler.handleHttpError(error, metadata);

    if (result.notification) {
      this.errorSubject.next(result.notification);
    }
  }

  private handleGenericError(error: unknown): void {
    const message = this.getErrorMessage(error);
    this.notifyError({
      type: 'error',
      title: 'Fehler',
      message: message || 'Ein unerwarteter Fehler ist aufgetreten.',
    });
  }

  private getErrorMessage(error: unknown): string | undefined {
    if (error && typeof error === 'object' && 'message' in error) {
      const errorObj = error as { message: unknown };
      return typeof errorObj.message === 'string'
        ? errorObj.message
        : undefined;
    }
    return undefined;
  }

  private notifyError(notification: ErrorNotification): void {
    this.errorSubject.next(notification);

    if (notification.details && notification.details.length > 0) {
      this.dialog.open(ErrorDialogComponent, {
        data: {
          title: notification.title,
          message: notification.message,
          details: notification.details,
        },
        panelClass: 'error-dialog',
        disableClose: false,
        autoFocus: true,
      });
    }
  }
}
